import { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import axios from '../utils/axios';
import { TIMEZONES } from '../components/meeting/MeetingTimezoneSelector';

// 미팅 예약 관련 커스텀 훅
export default function useMeetingScheduler(calendarId, calendarCode) {
  // 브라우저의 타임존을 가져오고, 지원되지 않으면 Asia/Seoul을 기본값으로 사용
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isSupportedTimezone = TIMEZONES.some(tz => tz.value === browserTimezone);
  const defaultTimezone = isSupportedTimezone ? browserTimezone : 'Asia/Seoul';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [userTimezone, setUserTimezone] = useState(defaultTimezone);
  const [calendarInfo, setCalendarInfo] = useState(null);

  // 가능한 시간 조회
  const fetchAvailableTimes = useCallback(async () => {
    if (!calendarId && !calendarCode) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let url;
      if (calendarId) {
        // calendar ID 사용
        url = `/api/meet/calendars/${calendarId}/available-times/`;
      } else if (calendarCode) {
        // calendar_code를 ID처럼 사용
        url = `/api/meet/calendars/${calendarCode}/available-times/`;
      }
      
      const response = await axios.get(url);
      const { available_times, meeting_name, calendar_code } = response.data;
      
      if (!available_times) {
        throw new Error('No available times in response');
      }
      
      setCalendarInfo({
        meetingName: meeting_name,
        calendar_code: calendar_code,
        calendarCode: response.data.calendar_code || calendarCode
      });
      
      // 시간대별로 그룹화
      const groupedTimes = groupTimesByDate(available_times, userTimezone);
      setAvailableTimes(groupedTimes);
      setError(null); // 성공 시 에러 상태 초기화
      
    } catch (err) {
      setError(err.response?.data?.error || '가능한 시간을 불러오는데 실패했습니다.');
      console.error('Failed to fetch available times:', err);
    } finally {
      setLoading(false);
    }
  }, [calendarId, calendarCode, userTimezone]);

  // 날짜별로 시간 그룹화
  const groupTimesByDate = (times, timezone) => {
    const grouped = {};

    if (!times || !Array.isArray(times)) {
      console.error('Invalid times data:', times);
      return grouped;
    }

    times.forEach(slot => {
      const { date, start, end, weekend, holiday } = slot;

      try {
        // 정각 시간만 생성 (매시 00분)
        // start가 정각이면 해당 시간 추가
        const startTime = parseISO(`${date}T${start}:00+09:00`);
        const startMinutes = parseInt(start.split(':')[1]);

        if (startMinutes === 0) {
          // start가 정각인 경우 그 시간을 추가
          const localTime = utcToZonedTime(startTime, timezone);

          // 변환된 시간의 실제 날짜를 구함
          const localDate = format(localTime, 'yyyy-MM-dd');

          // 해당 날짜의 그룹이 없으면 생성
          if (!grouped[localDate]) {
            grouped[localDate] = [];
          }

          grouped[localDate].push({
            originalTime: start, // KST 기준 원본 시간
            originalDate: date, // KST 기준 원본 날짜
            localTime: format(localTime, 'HH:mm'),
            isoTime: startTime.toISOString(),
            displayTime: format(localTime, 'HH:mm'),
            date: localDate, // 변환된 날짜 사용
            isWeekendOrHoliday: weekend || holiday
          });
        }
      } catch (err) {
        console.error('Error processing time slot:', slot, err);
      }
    });

    return grouped;
  };

  // 미팅 예약
  const scheduleMeeting = async (additionalData = {}) => {
    if (!selectedDate || !selectedTime) {
      throw new Error('날짜와 시간을 선택해주세요.');
    }

    setLoading(true);
    setError(null);

    try {
      let url;
      // 원본 날짜와 시간이 있으면 사용, 없으면 선택된 값 사용
      const requestBody = {
        date: additionalData.originalDate || selectedDate,
        time: additionalData.originalTime || selectedTime,
        duration_minutes: additionalData.duration_minutes || additionalData.duration || 40,
        ...additionalData
      };
      // originalDate와 originalTime은 requestBody에서 제거
      delete requestBody.originalDate;
      delete requestBody.originalTime;
      
      if (calendarId) {
        // calendar ID 사용
        url = `/api/meet/calendars/${calendarId}/schedule/`;
      } else if (calendarCode) {
        // calendar_code를 ID처럼 사용
        url = `/api/meet/calendars/${calendarCode}/schedule/`;
      }
      
      const response = await axios.post(url, requestBody);
      
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || '미팅 예약에 실패했습니다.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 선택한 날짜의 가능한 시간 목록
  const getTimesForDate = useCallback((date) => {
    return availableTimes[date] || [];
  }, [availableTimes]);

  // 가능한 날짜 목록
  const availableDates = Object.keys(availableTimes).sort();

  useEffect(() => {
    fetchAvailableTimes();
  }, [fetchAvailableTimes]);

  return {
    loading,
    error,
    availableTimes,
    availableDates,
    selectedDate,
    selectedTime,
    userTimezone,
    calendarInfo,
    setSelectedDate,
    setSelectedTime,
    setUserTimezone,
    getTimesForDate,
    fetchAvailableTimes,
    scheduleMeeting
  };
}